import React, { useEffect } from 'react'
import Box from '../components/Box'
import theme from '../libs/theme'
import { useMediaQuery } from '@mui/material'

const TestPage: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    // Load the activity loader script
    const script = document.createElement('script')
    //script.src = 'https://localhost:5173/scripts/activityLoader.js'
    //script.src = 'https://192.168.68.64:5173/scripts/activityLoader.js'
    script.src = ' https://app.coursely.se/scripts/activityLoader.js'

    script.defer = true

    script.onload = () => console.log('✅ Script loaded successfully')
    script.onerror = (error) => console.error('❌ Script failed to load:', error)

    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return (
    <Box style={styles.pageContainer}>
      {/* Top Content with Filler Text */}
      <Box style={styles.topContent}>
        <h1>Test Page - Backward Compatibility & Multiple Iframes</h1>
        <p>This page demonstrates both backward compatibility and multiple iframe containers.</p>
        <ul>
          <li>
            <strong>Legacy ID-based:</strong> Single iframe with id="container-iframe"
          </li>
          <li>
            <strong>New Class-based:</strong> Multiple iframes with class="container-iframe"
          </li>
          <li>
            <strong>Backward Compatible:</strong> Script works with both approaches
          </li>
        </ul>
      </Box>

      <Box style={styles.mainContent} direction={isMobile ? 'column' : 'row'}>
        {/* Left Test Content */}
        <Box style={styles.sideContent} width={isMobile ? '100%' : '20%'}>
          <h3>Left Panel</h3>
          <p>Some additional text for testing on the left side.</p>
        </Box>

        {/* Legacy ID-based iframe (Single) */}
        <Box style={styles.iframeContainer}>
          <h4>Legacy ID-based (Single iframe)</h4>
          <div
            id="container-iframe"
            style={styles.iframeContainer}
            data-client-id={'2smyyb7im'}
            data-activity-type={'Course'}
            data-view-mode={'grid'}
            data-hide-filter={'true'}
          ></div>
        </Box>

        {/* Right Test Content */}
        <Box style={styles.sideContent} width={isMobile ? '100%' : '20%'}>
          <h3>Right Panel</h3>
          <p>Some random text for testing on the right side.</p>
        </Box>
      </Box>

      {/* Multiple Class-based iframes */}
      <Box style={styles.bottomContent}>
        <h4>Multiple Class-based Iframes</h4>
        <Box style={styles.multipleIframesContainer}>
          {/* First Class-based iframe */}
          <Box style={styles.singleIframeContainer}>
            <h5>Iframe 1 - Courses Only</h5>
            <div style={styles.innerContainer}>
              <div
                className="container-iframe"
                style={styles.iframeContainer}
                data-client-id={'2smyyb7im'}
                data-activity-type={'Course'}
                data-view-mode={'grid'}
                data-hide-filter={'true'}
              ></div>
            </div>
          </Box>

          {/* Second Class-based iframe */}
          <Box style={styles.singleIframeContainer}>
            <h5>Iframe 2 - Events Only</h5>
            <div style={styles.innerContainer}>
              <div
                className="container-iframe"
                style={styles.iframeContainer}
                data-client-id={'2smyyb7im'}
                data-activity-type={'Occasion'}
                data-view-mode={'list'}
                data-hide-filter={'false'}
              ></div>
            </div>
          </Box>

          {/* Third Class-based iframe */}
          <Box style={styles.singleIframeContainer}>
            <h5>Iframe 3 - Week View</h5>
            <div style={styles.innerContainer}>
              <div
                className="container-iframe"
                style={styles.iframeContainer}
                data-client-id={'2smyyb7im'}
                data-view-mode={'week'}
                data-hide-filter={'false'}
              ></div>
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// Inline Styles
const styles: { [key: string]: React.CSSProperties } = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    fontFamily: "'Plus Jakarta Sans', sans-serif"
  },
  topContent: {
    width: '80%',
    textAlign: 'center',
    marginBottom: '20px'
  },
  mainContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    gap: '20px',
    marginBottom: '20px'
  },
  sideContent: {
    padding: '10px',
    backgroundColor: '#f0f0f0',
    textAlign: 'center',
    borderRadius: '5px'
  },
  iframeContainer: {
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    minHeight: '400px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    padding: '10px'
  },
  bottomContent: {
    width: '80%',
    textAlign: 'center'
  },
  multipleIframesContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  singleIframeContainer: {
    flex: '1 1 300px',
    minWidth: '300px',
    maxWidth: '400px',
    border: '3px solid #007bff',
    borderRadius: '8px',
    padding: '10px',
    backgroundColor: '#f8f9fa'
  },
  innerContainer: {
    border: '2px solid #ff0000',
    borderRadius: '6px',
    padding: '8px',
    backgroundColor: '#fff5f5',
    marginBottom: '10px'
  },
  heightDisplay: {
    marginTop: '10px',
    padding: '8px 12px',
    backgroundColor: '#e8f5e8',
    border: '1px solid #c3e6cb',
    borderRadius: '4px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#155724'
  },
  heightSummary: {
    width: '80%',
    marginTop: '30px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  },
  summaryGrid: {
    display: 'flex',
    justifyContent: 'space-around',
    flexWrap: 'wrap',
    gap: '20px',
    marginTop: '15px'
  },
  summaryItem: {
    padding: '10px 15px',
    backgroundColor: '#ffffff',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    minWidth: '120px',
    textAlign: 'center'
  }
}

export default TestPage
